import React, { useMemo, forwardRef } from 'react';
import { CircularProgress, FormControl, InputLabel, FormHelperText, Alert } from '@mui/material';
import ReactSelect from 'react-select';
import useAssignedModules from '../_data_sources/useAssignedModules';
import { neutral } from '_lex/_foundations/phxColors';

export type ModulePickerProps = {
  id: string;
  label?: string;
  value?: number | null;
  onChange: (value: number | null) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  size?: string;
  sx?: any;
};

const ModulePicker = forwardRef<HTMLDivElement, ModulePickerProps>(
  ({ id, label, value, onChange, placeholder, disabled, required, error, helperText, size, sx }, ref) => {
    const { data: assignedModules = [], isLoading: loading, error: apiError } = useAssignedModules();

    const options = useMemo(() => {
      return (assignedModules ?? []).map((module) => ({
        value: module.id,
        label: module.name,
        module,
      }));
    }, [assignedModules]);

    // Find the selected option based on the value (module ID)
    const selectedValue = useMemo(() => {
      return options.find((option) => option.value === value) || null;
    }, [value, options]);

    // Custom styles for react-select
    const customStyles = useMemo(
      () => ({
        menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
        menu: (base: any) => ({ ...base, zIndex: 9999 }),
        input: (base: any) => ({
          ...base,
          boxShadow: 'none',
          outline: 0,
          minHeight: size === 'small' ? 26 : 45,
        }),
        control: (base: any, state: any) => ({
          ...base,
          backgroundColor: disabled ? neutral[100] : base.backgroundColor,
          borderColor: disabled ? neutral[300] : base.borderColor,
          color: disabled ? 'rgba(0, 0, 0, 0.38)' : base.color,
          cursor: disabled ? 'not-allowed' : base.cursor,
          '&:hover': {
            borderColor: disabled ? neutral[300] : base['&:hover']?.borderColor,
          },
        }),
        singleValue: (base: any) => ({
          ...base,
          color: disabled ? 'rgba(0, 0, 0, 0.38)' : base.color,
        }),
        placeholder: (base: any) => ({
          ...base,
          color: disabled ? 'rgba(0, 0, 0, 0.38)' : base.color,
        }),
      }),
      [disabled, size]
    );

    if (loading) return <CircularProgress />;

    if (apiError) {
      return <Alert severity='error'>Failed to load assigned modules</Alert>;
    }

    return (
      <FormControl
        fullWidth
        error={error}
        className={error ? 'Mui-error' : ''}
        sx={sx}
        ref={ref}
      >
        {!!label && <InputLabel required={required}>{label}</InputLabel>}
        <ReactSelect
          name={id}
          value={selectedValue}
          options={options}
          placeholder={placeholder}
          isDisabled={disabled}
          classNamePrefix='react-select'
          menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
          styles={customStyles}
          onChange={(newValue) => {
            if (newValue && typeof newValue === 'object' && 'value' in newValue) {
              onChange(newValue.value as number);
            } else {
              onChange(null);
            }
          }}
        />
        {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }
);

ModulePicker.displayName = 'ModulePicker';

export default ModulePicker;
